// Company Onboarding Wizard — Phase 5.
//
// Single-page multi-step wizard that walks the operator through a
// tenant bootstrap. Each step auto-saves to the backend on "Save &
// Continue" so reloads / browser crashes never lose progress.
//
// Architecture:
//   • Top-level state holds the draft_id + per-step form snapshots.
//   • Each step component is dumb: it receives ``initialData`` and an
//     ``onSubmit(payload)`` callback, and that's it. No fetches.
//   • This page owns ALL API calls and ALL navigation logic.
//
// V1 ships StepIdentity as a fully working step; the other steps are
// minimal scaffolding so the wizard navigates end-to-end and the
// complete() call has a draft with at least identity to work with.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import {
  Sparkles, Building2, Users, Wallet, Layers, Database, Eye,
  ChevronRight, ChevronLeft, Loader2, CheckCircle2, RotateCcw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

import StepIdentity from './steps/StepIdentity';
import StepStructure from './steps/StepStructure';
import StepSize from './steps/StepSize';
import StepBanks from './steps/StepBanks';
import StepModules from './steps/StepModules';
import StepData from './steps/StepData';
import StepUsers from './steps/StepUsers';
import StepReview from './steps/StepReview';


const STEPS = [
  { id: 'identity',  label: 'Identity',  icon: Building2, component: StepIdentity },
  { id: 'structure', label: 'Structure', icon: Layers,    component: StepStructure },
  { id: 'size',      label: 'Size',      icon: Users,     component: StepSize },
  { id: 'banks',     label: 'Banks',     icon: Wallet,    component: StepBanks },
  { id: 'modules',   label: 'Modules',   icon: Sparkles,  component: StepModules },
  { id: 'data',      label: 'Data',      icon: Database,  component: StepData },
  { id: 'users',     label: 'Users',     icon: Users,     component: StepUsers },
  { id: 'review',    label: 'Review',    icon: Eye,       component: StepReview },
];


export default function CompanyOnboarding() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [draftId, setDraftId] = useState(params.get('draft') || null);
  const [draft, setDraft] = useState(null);                  // server-side draft
  const [activeIdx, setActiveIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completedInfo, setCompletedInfo] = useState(null);

  // ── Bootstrap ──────────────────────────────────────────────────────
  const startNew = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.post('/onboarding/start', { source: 'wizard' });
      setDraftId(data.id);
      setDraft(data);
      setParams({ draft: data.id });
      setActiveIdx(0);
    } catch {
      toast.error('Failed to start onboarding');
    } finally {
      setBusy(false);
    }
  }, [setParams]);

  const loadDraft = useCallback(async (id) => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.get(`/onboarding/draft/${id}`);
      setDraft(data);
      // Jump to the next-unfinished step.
      const idx = STEPS.findIndex((s) => s.id === (data.current_step || 'identity'));
      setActiveIdx(idx >= 0 ? idx : 0);
      if (data.status === 'completed') {
        setCompletedInfo({
          company_id: data.company_id,
          company_profile_id: data.company_profile_id,
          legal_name: (data.steps?.identity || {}).name_en || (data.steps?.identity || {}).name_ar,
        });
      }
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 403) {
        toast.error('Draft not found — starting a new one');
        startNew();
      } else {
        toast.error('Failed to load onboarding draft');
      }
    } finally {
      setBusy(false);
    }
  }, [startNew]);

  useEffect(() => {
    const h = setTimeout(() => {
      if (draftId) loadDraft(draftId);
      else startNew();
    }, 0);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Step submit ────────────────────────────────────────────────────
  const submitStep = async (stepId, payload) => {
    if (!draftId) return;
    setBusy(true);
    try {
      const { data } = await axiosInstance.post(
        `/onboarding/draft/${draftId}/step`,
        { step: stepId, payload },
      );
      setDraft(data);
      // Advance to next step if not already at the last one.
      const idx = STEPS.findIndex((s) => s.id === stepId);
      if (idx >= 0 && idx < STEPS.length - 1) {
        setActiveIdx(idx + 1);
      }
      toast.success(`${STEPS[idx]?.label} saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save step');
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => setActiveIdx((i) => Math.max(0, i - 1));
  const goForward = () => setActiveIdx((i) => Math.min(STEPS.length - 1, i + 1));

  const completeWizard = async () => {
    if (!draftId) return;
    setCompleting(true);
    try {
      const { data } = await axiosInstance.post(`/onboarding/draft/${draftId}/complete`);
      setCompletedInfo(data);
      toast.success(`Company "${data.legal_name || data.company_code}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setCompleting(false);
    }
  };

  const resetAndStartNew = async () => {
    setCompletedInfo(null);
    setDraftId(null);
    setDraft(null);
    setActiveIdx(0);
    setParams({});
    await startNew();
  };

  // ── Render ─────────────────────────────────────────────────────────
  const ActiveStep = STEPS[activeIdx].component;
  const stepState = draft?.steps?.[STEPS[activeIdx].id] || null;
  const completedSet = useMemo(
    () => new Set(draft?.completed_steps || []),
    [draft?.completed_steps],
  );

  return (
    <div
      data-testid="onboarding-wizard"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4 sm:p-6"
      dir="ltr"
    >
      <div className="max-w-5xl mx-auto space-y-6">
        <Header
          draftId={draftId}
          legalName={(draft?.steps?.identity || {}).name_en || (draft?.steps?.identity || {}).name_ar}
          completed={!!completedInfo}
        />

        {completedInfo ? (
          <CompletedBanner
            info={completedInfo}
            onStartNew={resetAndStartNew}
            onGoToDashboard={() => navigate('/cfo-dashboard')}
          />
        ) : (
          <>
            <StepProgress
              steps={STEPS}
              activeIdx={activeIdx}
              completedSet={completedSet}
              onJump={(i) => setActiveIdx(i)}
            />

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-7">
              {busy && !draft ? (
                <div className="py-10 flex items-center justify-center gap-2 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin" /> Preparing wizard…
                </div>
              ) : (
                <ActiveStep
                  initialData={stepState || {}}
                  draft={draft}
                  busy={busy}
                  onSubmit={(payload) => submitStep(STEPS[activeIdx].id, payload)}
                  onBack={activeIdx > 0 ? goBack : null}
                />
              )}
            </div>

            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={goBack}
                disabled={activeIdx === 0 || busy}
                data-testid="wizard-back-btn"
              >
                <ChevronLeft size={14} className="mr-1" /> Back
              </Button>
              <div className="flex gap-2">
                {activeIdx < STEPS.length - 1 ? (
                  <Button
                    variant="outline"
                    onClick={goForward}
                    disabled={busy}
                    data-testid="wizard-next-btn"
                  >
                    Skip → <ChevronRight size={14} className="ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={completeWizard}
                    disabled={completing || !completedSet.has('identity')}
                    data-testid="wizard-complete-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {completing
                      ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Creating company…</>
                      : <><CheckCircle2 size={14} className="mr-1.5" /> Complete onboarding</>}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ─── Header ──────────────────────────────────────────────────────────────


function Header({ draftId, legalName, completed }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-sm">
            <Sparkles size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight" data-testid="onboarding-title">
              Company Onboarding
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {completed
                ? 'Tenant successfully bootstrapped. Continue to the dashboard.'
                : 'Walk through the steps to bootstrap a new tenant. Every step auto-saves.'}
            </p>
          </div>
        </div>
        <div className="text-right text-xs text-slate-500 space-y-0.5">
          {draftId && <div data-testid="onboarding-draft-id">Draft: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{draftId}</code></div>}
          {legalName && <div className="font-semibold text-slate-700" data-testid="onboarding-legal-name">{legalName}</div>}
        </div>
      </div>
    </div>
  );
}


// ─── Step progress bar ──────────────────────────────────────────────────


function StepProgress({ steps, activeIdx, completedSet, onJump }) {
  return (
    <div data-testid="step-progress" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
      <ol className="flex items-center gap-2 overflow-x-auto">
        {steps.map((s, i) => {
          const isDone = completedSet.has(s.id);
          const isActive = i === activeIdx;
          const Icon = s.icon;
          return (
            <li key={s.id} className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                data-testid={`step-${s.id}`}
                onClick={() => onJump(i)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition ${
                  isActive
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : isDone
                      ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  isActive ? 'bg-white/20' :
                  isDone ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-200 text-slate-500'
                }`}>
                  {isDone ? <CheckCircle2 size={12} /> : <Icon size={12} />}
                </span>
                <span className="uppercase tracking-wider">{s.label}</span>
              </button>
              {i < steps.length - 1 && (
                <ChevronRight size={12} className="text-slate-300" />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}


// ─── Completed banner ──────────────────────────────────────────────────


function CompletedBanner({ info, onStartNew, onGoToDashboard }) {
  const users = info.users_provisioned || [];
  const modules = info.modules_enabled || [];
  return (
    <div
      data-testid="onboarding-completed"
      className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 shadow-sm"
    >
      <div className="flex items-start gap-4">
        <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-700">
          <CheckCircle2 size={22} />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-emerald-900">
            {info.legal_name || info.company_code || 'Company'} is live!
          </h2>
          <p className="text-sm text-emerald-800 mt-1">
            Tenant created with company_id <code className="bg-white/70 px-1.5 py-0.5 rounded text-xs">{info.company_id}</code>.
          </p>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-white/70 rounded-lg p-3 border border-emerald-200">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Users provisioned</div>
              <div className="text-lg font-bold text-emerald-900 mt-0.5" data-testid="completed-users-count">{users.length}</div>
              <ul className="mt-1 space-y-0.5 text-xs text-slate-700">
                {users.map((u) => (
                  <li key={u.id} data-testid={`completed-user-${u.email}`}>
                    {u.email} <span className="text-slate-400">· {u.role}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/70 rounded-lg p-3 border border-emerald-200">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Modules enabled</div>
              <div className="text-lg font-bold text-emerald-900 mt-0.5" data-testid="completed-modules-count">{modules.length}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {modules.map((m) => (
                  <span key={m} data-testid={`completed-module-${m}`} className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">{m}</span>
                ))}
              </div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 border border-emerald-200">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Banks</div>
              <div className="text-lg font-bold text-emerald-900 mt-0.5" data-testid="completed-bank-count">{info.bank_count || 0}</div>
              <div className="mt-1 text-xs text-slate-600">accounts persisted</div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={onGoToDashboard} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="goto-dashboard-btn">
              Go to dashboard <ChevronRight size={14} className="ml-1" />
            </Button>
            <Button variant="outline" onClick={onStartNew} data-testid="start-another-btn">
              <RotateCcw size={14} className="mr-1.5" /> Onboard another company
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
